/**
 * Utilitaire centralisé pour générer les URLs des médias multi-tenant
 * Structure: REACT_APP_MEDIA_URL/{club_slug}/{type}/{filename}
 */

const MEDIA_BASE_URL = process.env.REACT_APP_MEDIA_URL || '';

/**
 * Récupère le slug du club depuis localStorage
 * @returns {string|null} - Le slug du club ou null
 */
export const getClubSlug = () => {
  // App mono-club « VBC Femenino » : slug figé (fallback même avant login).
  return localStorage.getItem('clubSlug') || 'vbc-femenino';
};

/**
 * Retourne l'URL de base des médias avec le club_slug intégré
 * Permet de remplacer facilement: `${mediaUrl}/photos/...` par `${getMediaBaseUrl()}/photos/...`
 * @returns {string} - URL de base avec club_slug (ex: "http://valenciaapp.fr/valencia")
 */
export const getMediaBaseUrl = () => {
  const clubSlug = getClubSlug();
  if (clubSlug) {
    return `${MEDIA_BASE_URL}/${clubSlug}`;
  }
  // Fallback si pas de club
  return `${MEDIA_BASE_URL}/default`;
};

/**
 * Construit l'URL d'un média
 * @param {string} type - Type de média ('photos', 'users', 'equipes', 'competitions', 'cantera')
 * @param {string} filename - Nom du fichier
 * @param {boolean} bustCache - Ajouter un timestamp pour éviter le cache (default: false)
 * @returns {string} - URL complète du média
 */
export const getMediaUrl = (type, filename, bustCache = false) => {
  const clubSlug = getClubSlug();

  // Construction du chemin
  let url;
  if (clubSlug) {
    url = `${MEDIA_BASE_URL}/${clubSlug}/${type}/${filename}`;
  } else {
    // Fallback si pas de club (utilisateur sans club ou mode legacy)
    url = `${MEDIA_BASE_URL}/default/${type}/${filename}`;
  }

  // Ajouter cache buster si demandé
  if (bustCache) {
    url += `?${new Date().getTime()}`;
  }

  return url;
};

/**
 * URL pour les photos de joueurs
 * @param {number|string} playerId - ID du joueur
 * @param {boolean} bustCache - Forcer le rafraîchissement du cache
 * @returns {string}
 */
export const getPlayerPhotoUrl = (playerId, bustCache = true) => {
  // Mode demo: avatar générique au lieu de la vraie photo
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  if (user && user.is_demo) {
    return `https://ui-avatars.com/api/?name=${playerId}&background=random&size=150`;
  }
  return getMediaUrl('photos', `${playerId}.png`, bustCache);
};

/**
 * URL pour les avatars utilisateurs
 * @param {number|string} userId - ID de l'utilisateur
 * @param {boolean} bustCache - Forcer le rafraîchissement du cache
 * @returns {string}
 */
export const getUserAvatarUrl = (userId, bustCache = true) => {
  return getMediaUrl('users', `${userId}.png`, bustCache);
};

/**
 * URL pour les logos d'équipes
 * @param {number|string} teamId - ID de l'équipe
 * @param {boolean} bustCache - Forcer le rafraîchissement du cache
 * @returns {string}
 */
export const getTeamLogoUrl = (teamId, bustCache = false) => {
  return getMediaUrl('equipes', `${teamId}.png`, bustCache);
};

/**
 * URL pour les logos de compétitions
 * @param {number|string} competitionId - ID de la compétition
 * @param {boolean} bustCache - Forcer le rafraîchissement du cache
 * @returns {string}
 */
export const getCompetitionLogoUrl = (competitionId, bustCache = false) => {
  return getMediaUrl('competitions', `${competitionId}.png`, bustCache);
};

/**
 * URL pour les photos de groupes/cantera
 * @param {number|string} groupId - ID du groupe
 * @param {boolean} bustCache - Forcer le rafraîchissement du cache
 * @returns {string}
 */
export const getGroupPhotoUrl = (groupId, bustCache = true) => {
  return getMediaUrl('cantera', `${groupId}.jpg`, bustCache);
};

export default {
  getClubSlug,
  getMediaBaseUrl,
  getMediaUrl,
  getPlayerPhotoUrl,
  getUserAvatarUrl,
  getTeamLogoUrl,
  getCompetitionLogoUrl,
  getGroupPhotoUrl
};
